/*
  The header stats ticker's items (Robert, 2026-10-20: "cool stats across the top here that
  keep updating. like Most graded TCG, Sports … Whats hot that sort of thing").

  Pure so it is unit-tested (component tests cannot run in this repo). Every figure comes from
  a public endpoint the site already calls or one sibling of it; nothing is estimated:

    most graded TCG / sport   /v2/catalog/games?scope=population  cardsGraded + category
    top set                   /v2/catalog/sets?scope=population   cardsGraded (the API's
                                                                   default order is most graded first)
    graded this month         /v1/counts                          monthGraded
    Pristine 10s              /v1/counts                          pristine10Count
    hot right now             /v1/gradings?limit=200&sort=-completedOn, grouped by set
    latest Flawless           /v1/gradings?grade=Flawless&sort=-completedOn
    just certified            /v1/gradings?limit=8&sort=-completedOn (the home card's feed)

  "Most graded card" is not here: no public endpoint ranks cards across the whole catalogue.

  An item whose source is missing or unusable is left out rather than shown as 0, so a slow or
  failing endpoint shortens the ticker instead of putting a wrong figure in it.
*/

import { isMintGameCode, resolveGameCategory } from "./categories";
import type { HomeCategory } from "./categories";
import { formatCount } from "./format";
import { monthGradedLabel, selectJustCertified, selectMonthGraded } from "./heroStats";

export interface HeaderStat {
  /* Stable per kind of stat, so React keeps a chip across polls. */
  key: string;
  /* Small caps lead-in: "Most graded TCG". */
  label: string;
  /* The headline: a name or a grade and card. */
  value: string;
  /* A figure shown in gold after the value, already formatted. Empty when there is none. */
  figure: string;
  /* Muted trailing context: "Pokémon", "PCG Japan". Empty when there is none. */
  detail: string;
  /* An in-app route the chip links to, or "" for none. */
  to: string;
}

/* Just enough of the two v2 rows. The payloads have no contract, so every field is checked. */
interface GameRow {
  id?: unknown;
  name?: unknown;
  gameCode?: unknown;
  category?: unknown;
  cardsGraded?: unknown;
  archived?: unknown;
}

interface SetRow {
  name?: unknown;
  gameId?: unknown;
  gameName?: unknown;
  cardsGraded?: unknown;
  archived?: unknown;
}

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const count = (value: unknown): number | null =>
  typeof value === "number" && isFinite(value) && value > 0 ? value : null;

const rowsOf = (rows: unknown): Record<string, unknown>[] =>
  Array.isArray(rows)
    ? rows.filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    : [];

/* Live public games only: not New Zealand Mint (OVD16), not archived, with a code to link to. */
const liveGames = (games: unknown): GameRow[] =>
  rowsOf(games).filter((game: GameRow) => {
    const code = text(game.gameCode);
    return !!code && !isMintGameCode(code) && game.archived !== true;
  });

export interface TopGame {
  name: string;
  gameCode: string;
  cardsGraded: number;
}

/*
  The most graded live game in one home section. Uses the home page's own category rule, so a
  game with no category counts as a TCG here exactly as it is listed under TCGs there.
  A tie keeps the game the API lists first.
*/
export const selectTopGame = (games: unknown, section: HomeCategory): TopGame | null => {
  let best: TopGame | null = null;
  for (const game of liveGames(games)) {
    const category = typeof game.category === "string" ? game.category : null;
    const graded = count(game.cardsGraded);
    if (graded === null || resolveGameCategory(category) !== section) {
      continue;
    }
    if (!best || graded > best.cardsGraded) {
      best = { name: text(game.name) || text(game.gameCode), gameCode: text(game.gameCode), cardsGraded: graded };
    }
  }
  return best;
};

export interface TopSet {
  setName: string;
  gameName: string;
  gameCode: string;
  cardsGraded: number;
}

/*
  The most graded set across every game, from the first page of /v2/catalog/sets. The API
  already sends the most graded first; the max is taken anyway so a change of default order
  degrades to "most graded on this page" rather than to whatever row came first.

  A set links to its game by code, which the sets payload does not carry, so a set whose game
  is not a live game in the games payload is skipped. That is also what keeps Mint out.
*/
export const selectTopSet = (sets: unknown, games: unknown): TopSet | null => {
  const codes = new Map<number, string>();
  for (const game of liveGames(games)) {
    if (typeof game.id === "number") {
      codes.set(game.id, text(game.gameCode));
    }
  }

  let best: TopSet | null = null;
  for (const set of rowsOf(sets) as SetRow[]) {
    const graded = count(set.cardsGraded);
    const gameCode = typeof set.gameId === "number" ? codes.get(set.gameId) : undefined;
    const setName = text(set.name);
    if (graded === null || !gameCode || !setName || set.archived === true) {
      continue;
    }
    if (!best || graded > best.cardsGraded) {
      best = { setName, gameName: text(set.gameName), gameCode, cardsGraded: graded };
    }
  }
  return best;
};

export interface HotSet {
  setName: string;
  gameName: string;
  gameCode: string;
  /* Certified cards from this set among the rows read. */
  count: number;
  /* Certified rows read in all, the "of the last N". */
  window: number;
}

/* Fewer than this many from one set is not "hot", it is just the last order. */
export const HOT_MIN_COUNT = 3;

interface FeedRow {
  completed?: unknown;
  grade?: unknown;
  gameName?: unknown;
  game?: unknown;
  setName?: unknown;
  cardInfo?: { gameCode?: unknown; setName?: unknown } | null;
}

/*
  "Hot right now": the set with the most certified cards in the newest completions.

  Counted on certified rows only (completed, with a grade: selectJustCertified's rule), with
  Mint dropped. Grouped by game code and the catalogue set name in cardInfo, which is what the
  set page's route takes (CardLink builds its links the same way). A tie goes to the set
  that reached the count first, i.e. the more recent one.
*/
export const selectHotSet = (rows: unknown): HotSet | null => {
  const tallies = new Map<string, HotSet>();
  let window = 0;

  for (const row of rowsOf(rows) as FeedRow[]) {
    if (row.completed !== true || !text(row.grade)) {
      continue;
    }
    const gameCode = text(row.cardInfo?.gameCode);
    if (!gameCode || isMintGameCode(gameCode) || isMintGameCode(text(row.game))) {
      continue;
    }
    window += 1;
    const setName = text(row.cardInfo?.setName) || text(row.setName);
    if (!setName) {
      continue;
    }
    const key = `${gameCode.toLowerCase()}\u0000${setName}`;
    const tally = tallies.get(key);
    if (tally) {
      tally.count += 1;
    } else {
      tallies.set(key, { setName, gameName: text(row.gameName) || text(row.game), gameCode, count: 1, window: 0 });
    }
  }

  let best: HotSet | null = null;
  tallies.forEach(tally => {
    if (!best || tally.count > best.count) {
      best = tally;
    }
  });
  const hot = best as HotSet | null;
  if (!hot || hot.count < HOT_MIN_COUNT) {
    return null;
  }
  return { ...hot, window };
};

/*
  A set name as a route segment, the way helpers/routing's sanitizeURIPathSegment does it:
  only the slash is encoded, so these links match every other set link on the site.
*/
const setSegment = (setName: string): string => setName.replace(/\//g, "%2F");

export interface HeaderStatsInput {
  games?: unknown;
  sets?: unknown;
  counts?: unknown;
  /* /gradings rows, newest completions first. */
  recent?: unknown;
  /* The larger window "hot right now" reads. */
  hotWindow?: unknown;
  /* /gradings?grade=Flawless rows, newest first. */
  flawless?: unknown;
  now: Date;
}

/*
  The ticker's items, in reading order. Each is independent: a missing source drops its item
  only.
*/
export const selectHeaderStats = (input: HeaderStatsInput): HeaderStat[] => {
  const items: HeaderStat[] = [];

  const tcg = selectTopGame(input.games, "TCG");
  if (tcg) {
    items.push({
      key: "top-tcg",
      label: "Most graded TCG",
      value: tcg.name,
      figure: formatCount(tcg.cardsGraded),
      detail: "",
      to: `/${tcg.gameCode}`,
    });
  }

  const monthGraded = selectMonthGraded(input.counts);
  if (monthGraded !== null && monthGraded > 0) {
    items.push({
      key: "month",
      label: monthGradedLabel(input.now),
      value: "",
      figure: formatCount(monthGraded),
      detail: "cards",
      to: "",
    });
  }

  const hot = selectHotSet(input.hotWindow);
  if (hot) {
    items.push({
      key: "hot",
      label: "Hot right now",
      value: hot.setName,
      figure: "",
      detail: `${hot.count} of the last ${hot.window} certified`,
      to: `/${hot.gameCode}/${setSegment(hot.setName)}`,
    });
  }

  const sport = selectTopGame(input.games, "Sports");
  if (sport) {
    items.push({
      key: "top-sport",
      label: "Most graded sport",
      value: sport.name,
      figure: formatCount(sport.cardsGraded),
      detail: "",
      to: `/${sport.gameCode}`,
    });
  }

  const flawless = selectJustCertified(input.flawless).find(row => /^flawless\b/i.test(row.gradeLabel));
  if (flawless && flawless.cardName) {
    items.push({
      key: "flawless",
      label: "Latest Flawless 10",
      value: flawless.cardName,
      figure: "",
      detail: flawless.gameName,
      to: `/report/${flawless.gradedId}`,
    });
  }

  const topSet = selectTopSet(input.sets, input.games);
  if (topSet) {
    items.push({
      key: "top-set",
      label: "Most graded set",
      value: topSet.setName,
      figure: formatCount(topSet.cardsGraded),
      detail: topSet.gameName,
      to: `/${topSet.gameCode}/${setSegment(topSet.setName)}`,
    });
  }

  const latest = selectJustCertified(input.recent)[0];
  if (latest && latest.cardName) {
    items.push({
      key: "latest",
      label: "Just certified",
      value: `${latest.gradeLabel} · ${latest.cardName}`,
      figure: "",
      detail: latest.gameName,
      to: `/report/${latest.gradedId}`,
    });
  }

  const pristine =
    typeof input.counts === "object" && input.counts !== null
      ? count((input.counts as { pristine10Count?: unknown }).pristine10Count)
      : null;
  if (pristine !== null) {
    items.push({
      key: "pristine",
      label: "Pristine 10s graded",
      value: "",
      figure: formatCount(pristine),
      detail: "",
      to: "",
    });
  }

  return items;
};
